/**
 * Phase 15.6J — Global Mini Player UI.
 *
 * Bottom-fixed mini player that appears whenever a track is loaded into
 * the global player. Tapping the cover/title navigates to the full track
 * page; play/pause/seek/next/prev/close are inline.
 *
 * Mobile-first. Respects iPhone safe-area-inset-bottom. Never blocks the
 * mobile drawer or hamburger.
 */
import { useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { usePlayer } from "./PlayerProvider";
import ShuffleRadioActions from "./ShuffleRadioActions";
import { optimizeImage } from "@/lib/mediaUrls";

const fmt = (s) => {
  if (!isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const PlayIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor">
    <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
  </svg>
);
const PauseIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor">
    <rect x="5" y="3.5" width="3.5" height="13" rx="1" />
    <rect x="11.5" y="3.5" width="3.5" height="13" rx="1" />
  </svg>
);
const NextIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 5l9 7-9 7V5zm10 0h2v14h-2V5z" />
  </svg>
);
const PrevIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 19l-9-7 9-7v14zM8 19H6V5h2v14z" />
  </svg>
);

const GlobalMiniPlayer = () => {
  const {
    track, isPlaying, currentTime, duration, mediaError, isVisible,
    hasNext, hasPrev, sourceLabel,
    toggle, seekToPercent, next, prev, close, retry,
  } = usePlayer();
  const navigate = useNavigate();
  const location = useLocation();

  // Push body content up by the player height so the last item is reachable.
  // Player is ~64px tall + safe-area. We add 88px when visible.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (isVisible) {
      document.body.style.paddingBottom = "calc(88px + env(safe-area-inset-bottom))";
    } else {
      document.body.style.paddingBottom = "";
    }
    return () => { document.body.style.paddingBottom = ""; };
  }, [isVisible]);

  if (!isVisible || !track) return null;
  // Phase 20.6.2 — Hide the mini-player while the user is on the dedicated
  // full-screen radio player route; the page IS the player there.
  if (location.pathname.startsWith("/shuffle/radio-player")) return null;

  const progress = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;
  const onSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = (x / rect.width) * 100;
    seekToPercent(pct);
  };

  const goToTrack = () => {
    // Phase 20.6.2 — When the current track came from RGM Shuffle Radio,
    // expanding the mini-player should open the full-screen radio player
    // rather than the static detail page.
    const raw = track?.raw || {};
    if (raw._station) {
      navigate("/shuffle/radio-player");
      return;
    }
    if (raw.kind === "beat" && (raw.beat_id || raw.submission_id)) {
      navigate(`/beats/${raw.beat_id || raw.submission_id}`);
      return;
    }
    if (track?.trackId) navigate(`/track/${track.trackId}`);
  };

  return (
    <div
      data-testid="global-mini-player"
      role="region"
      aria-label="Now playing"
      className="fixed left-0 right-0 bottom-0 z-[80] bg-[#0d0d12]/95 backdrop-blur-xl border-t border-purple-500/30 shadow-[0_-8px_30px_rgba(120,40,200,0.25)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      {/* Progress bar (top edge, full width, click to seek) */}
      <div
        className="w-full h-1 bg-purple-900/40 cursor-pointer group"
        onClick={onSeek}
        data-testid="mini-player-progress"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress)}
      >
        <div
          className="h-full bg-gradient-to-r from-purple-500 to-pink-500 relative"
          style={{ width: `${progress}%` }}
        >
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>

      {/* Phase 20.6 — Shuffle Radio action bar (renders only when current
          track came from the radio queue). Sits above the playback row so
          the offset/safe-area math stays the same. */}
      <ShuffleRadioActions />

      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 flex items-center gap-3">
        {/* Cover (tap to open track) */}
        <button
          onClick={goToTrack}
          className="w-12 h-12 rounded-md overflow-hidden flex-shrink-0 bg-purple-900/40 border border-purple-500/30"
          data-testid="mini-player-open-track"
          aria-label="Open track page"
        >
          {track.coverUrl ? (
            <img src={optimizeImage(track.coverUrl, 'avatar')} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-purple-200 text-lg">🎵</div>
          )}
        </button>

        {/* Title + Artist + Source label (tap to open) */}
        <button
          onClick={goToTrack}
          className="flex-1 min-w-0 text-left"
          data-testid="mini-player-meta"
        >
          <p className="text-white font-semibold text-sm truncate" data-testid="mini-player-title">
            {track.title}
          </p>
          <p className="text-gray-400 text-xs truncate">
            {track.artistName}
            {sourceLabel && (
              <span className="hidden sm:inline text-purple-300/80"> · {sourceLabel}</span>
            )}
            {track.singlesLocked && (
              <span
                className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest bg-amber-500/15 border border-amber-400/40 text-amber-200 align-middle"
                data-testid="mini-player-preview-indicator"
                title="Preview only — full track locked"
              >
                🔒 Preview
              </span>
            )}
          </p>
        </button>

        {track.singlesLocked && track.singlesBuyUrl && (
          <Link
            to={track.singlesBuyUrl}
            onClick={(e) => e.stopPropagation()}
            className="hidden sm:inline-flex flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] font-black bg-gradient-to-r from-emerald-500 to-cyan-500 text-black hover:from-emerald-400 hover:to-cyan-400"
            data-testid="mini-player-buy-btn"
            data-listing-id={track.singlesListingId || ""}
          >
            Buy Track
          </Link>
        )}

        {/* Time (desktop only) */}
        <div className="hidden md:block text-xs tabular-nums text-gray-400 flex-shrink-0">
          <span data-testid="mini-player-time-current">{fmt(currentTime)}</span>
          <span> / </span>
          <span data-testid="mini-player-time-duration">{fmt(duration)}</span>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {hasPrev && (
            <button
              onClick={prev}
              className="w-8 h-8 rounded-full text-gray-300 hover:text-white hover:bg-purple-500/20 flex items-center justify-center"
              data-testid="mini-player-prev"
              aria-label="Previous track"
            >
              <PrevIcon />
            </button>
          )}
          {mediaError ? (
            <button
              onClick={retry}
              className="px-3 h-9 rounded-full text-xs font-semibold text-rose-100 bg-rose-500/20 hover:bg-rose-500/30 border border-rose-400/40"
              data-testid="mini-player-retry"
              aria-label="Retry playback"
            >
              ⚠ Retry
            </button>
          ) : (
            <button
              onClick={toggle}
              className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 flex items-center justify-center text-white shadow-lg shadow-purple-500/30"
              data-testid="mini-player-toggle"
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </button>
          )}
          {hasNext && (
            <button
              onClick={next}
              className="w-8 h-8 rounded-full text-gray-300 hover:text-white hover:bg-purple-500/20 flex items-center justify-center"
              data-testid="mini-player-next"
              aria-label="Next track"
            >
              <NextIcon />
            </button>
          )}
          <button
            onClick={close}
            className="w-8 h-8 rounded-full text-gray-400 hover:text-white hover:bg-rose-500/20 flex items-center justify-center"
            data-testid="mini-player-close"
            aria-label="Close player"
            title="Close mini player"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M6 6L18 18M6 18L18 6" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>

      {/* Error banner (subtle, only when failed) */}
      {mediaError === "audio_missing" && (
        <div className="px-4 pb-2 text-xs text-rose-200" data-testid="mini-player-error-missing">
          Track needs re-upload — no audio file available.
        </div>
      )}
    </div>
  );
};

export default GlobalMiniPlayer;
